/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   HomepageContainer
 */

import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { PAGE_NAMES } from '@zcode/mixpanel'
import { decryptToken } from '@zcode/nr/es/shared/api'
import { withGeoLocation } from '@zcode/nr/es/shared/components/GeoLocation'
import { selectIsLoggedIn } from '@zcode/nr/es/shared/modules/user/selectors'
import { withLoadData } from '@zcode/nr/es/shared/packages/load-data'
import { hocCompose, memoMapDispatchToProps } from '@zcode/react-utils'
import { routerConnect } from '@zcode/router-connect'
import type { RouteComponentProps } from '@zcode/router-utils'
import type { State } from '@zcode/state-type'
import { useEffect } from 'react'
import React from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { addSuccessButterBar } from '../../actions/butter.action'
import { selectIsCurrentSearchView, selectIsMessaging3Active } from '../../inspectors/index'
import { logger } from '../../logger'
import { thunksExternal } from '../../modules/app/thunks/external/index'
import {
  selectAreAddressLinksLoaded,
  selectAreBuildingLinksLoaded,
} from '../../modules/homepage/selectors/index'
import { mixpanelViewPage } from '../../modules/mixpanel/thunks/mixpanelViewPage'
import { loadHashtagContent } from '../../modules/modals/thunks/hashtagModal/loadHashtagContent'
import { loadUnreadCount } from '../../modules/stream/thunks/loadUnreadCount'
import { trackHomepageImpressionEvent } from '../../modules/traktor/thunks/trackHomepageImpressionEvent'
import type { Dispatch } from '../../modules/types'
import { stripUrlParam } from '../../utils/urls/stripUrlParam'
import Homepage, { type HomepageProps } from './Homepage'
import { loadData } from './loadData'
import { loadLinkpack } from './loadLinkpack'

const mapRouteToProps = (routeProps: RouteComponentProps) =>
  ({
    routeProps,
  }) satisfies Partial<HomepageProps>

const mapStateToProps = (state: State) => {
  const addressLinksLoaded = selectAreAddressLinksLoaded(state)
  const buildingLinksLoaded = selectAreBuildingLinksLoaded(state)
  return {
    linkpacksLoaded: addressLinksLoaded && buildingLinksLoaded,
    isLoggedIn: selectIsLoggedIn(state),
    isMessage3: selectIsMessaging3Active(state),
    isMobile: selectIsMobile(state),
  } satisfies Partial<HomepageProps>
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    loadHashtagContent: (routeProps: RouteComponentProps) =>
      dispatch(loadHashtagContent(routeProps)),
    loadLinkpacks: () => {
      return Promise.all([dispatch(loadLinkpack('addresses')), dispatch(loadLinkpack('buildings'))])
    },
    loadUnreadCount: async () => {
      return await dispatch(loadUnreadCount())
    },
    onMount: () => {
      dispatch(mixpanelViewPage({ pageName: PAGE_NAMES.HOMEPAGE }))
      dispatch(trackHomepageImpressionEvent())
      dispatch(thunksExternal())
    },
    onBlockedUserToken: (token: string | string[]) => {
      if (token === 'deprecated') {
        dispatch(
          addSuccessButterBar({
            message:
              'This proxy system has been disabled; there will be no new emails coming from zlead.co.',
          })
        )
      } else {
        if (Array.isArray(token)) {
          return
        }
        decryptToken
          .query({ token })
          .then(({ email }: { email: string }) => {
            dispatch(
              addSuccessButterBar({
                message: `Blocked email: ${email}`,
              })
            )

            stripUrlParam('/', 'blocked_user_token')
          })
          .catch((error: unknown) => {
            logger.debug('error occurred with decryptToken in HomepageContainer:', error)
          })
      }
    },
  } satisfies Partial<HomepageProps>
}

const equalProps = ['getGeoLocation' as const, 'isSafeForClientOnly' as const]
const memoMapHomepageDispatchToProps = memoMapDispatchToProps(mapDispatchToProps, equalProps)

const HomePageInnerContainer = (props: HomepageProps) => {
  const { isLoggedIn, isMessage3, loadHashtagContent, loadUnreadCount, routeProps } = props
  useEffect(() => {
    if (isLoggedIn && isMessage3) {
      loadUnreadCount()
    }
  }, [isLoggedIn, isMessage3, loadUnreadCount])
  const location = useLocation()
  useEffect(() => {
    loadHashtagContent(routeProps)
  }, [loadHashtagContent, routeProps])
  return <Homepage {...props} />
}

const HomepageContainer = hocCompose(
  withGeoLocation,
  withLoadData(loadData),
  routerConnect(mapRouteToProps),
  connect(mapStateToProps, memoMapHomepageDispatchToProps),
  HomePageInnerContainer
)

// eslint-disable-next-line import/no-default-export
export default HomepageContainer
